<template>
  <div style="max-width: 960px; margin: 0 auto; padding: 1.25rem;">

    <!-- Header -->
    <div style="margin-bottom: 1.25rem;">
      <h1 style="font-size: 1.5rem; font-weight: 700; color: #111827; margin: 0 0 0.25rem;">Campaigns</h1>
      <p style="font-size: 0.875rem; color: #6B7280; margin: 0;">
        {{ campaignsData.total ? campaignsData.total.toLocaleString() : '0' }} campaigns found
      </p>
    </div>

    <!-- Summary stats -->
    <div style="display: flex; gap: 0.75rem; margin-bottom: 1.25rem; flex-wrap: wrap;">
      <div style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ campaignsData.total ? campaignsData.total.toLocaleString() : '0' }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Results</div>
      </div>
      <div style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #111827;">{{ campaigns.length }}</div>
        <div style="font-size: 0.6875rem; color: #6B7280; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Loaded</div>
      </div>
      <div v-if="filterCount > 0" style="flex: 1; min-width: 100px; padding: 0.875rem 1rem; background: #FDF2F8; border: 1px solid #FBCFE8; border-radius: 0.625rem; text-align: center;">
        <div style="font-size: 1.5rem; font-weight: 700; color: #BE185D;">{{ filterCount }}</div>
        <div style="font-size: 0.6875rem; color: #9D174D; text-transform: uppercase; letter-spacing: 0.05em; font-weight: 500;">Filters Active</div>
      </div>
    </div>

    <!-- Search & Action bar -->
    <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.75rem; margin-bottom: 1.25rem; padding: 0.875rem 1rem; background: #FAFAFA; border: 1px solid #E5E7EB; border-radius: 0.625rem;">
      <div style="display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 200px;">
        <div style="position: relative; flex: 1;">
          <svg xmlns="http://www.w3.org/2000/svg" style="position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); width: 0.875rem; height: 0.875rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            v-model="searchTerm"
            v-on:keyup.enter="search"
            placeholder="Search by ID, name, email..."
            style="width: 100%; padding: 0.5rem 0.75rem 0.5rem 2.25rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; outline: none;"
            @focus="$event.target.style.borderColor='#EC4899'"
            @blur="$event.target.style.borderColor='#D1D5DB'"
          />
        </div>
        <button @click="search" style="padding: 0.5rem 1rem; background: #EC4899; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600; cursor: pointer; white-space: nowrap;" @mouseover="$event.target.style.background='#DB2777'" @mouseout="$event.target.style.background='#EC4899'">
          Search
        </button>
        <button @click="openFilterModal = true" style="padding: 0.5rem 1rem; background: #374151; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 500; cursor: pointer; white-space: nowrap; position: relative;" @mouseover="$event.target.style.background='#1F2937'" @mouseout="$event.target.style.background='#374151'">
          Filter
          <span v-if="filterCount > 0" style="position: absolute; top: -0.375rem; right: -0.375rem; width: 1.125rem; height: 1.125rem; background: #EC4899; color: white; border-radius: 50%; font-size: 0.625rem; font-weight: 700; display: flex; align-items: center; justify-content: center;">{{ filterCount }}</span>
        </button>
      </div>
      <button @click="download" style="padding: 0.5rem 1rem; background: white; color: #374151; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.8125rem; font-weight: 500; cursor: pointer; white-space: nowrap; display: flex; align-items: center; gap: 0.375rem;" @mouseover="$event.target.style.borderColor='#10B981'; $event.target.style.color='#10B981'" @mouseout="$event.target.style.borderColor='#D1D5DB'; $event.target.style.color='#374151'">
        <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.875rem; height: 0.875rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
        </svg>
        Export
      </button>
    </div>

    <!-- Results info -->
    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.75rem; padding: 0 0.25rem;">
      <span style="font-size: 0.75rem; color: #9CA3AF;">
        Showing {{ campaigns.length }} of {{ campaignsData.total || 0 }}
      </span>
    </div>

    <!-- Campaign list -->
    <div v-if="campaigns.length === 0" style="text-align: center; padding: 3rem 1rem; color: #9CA3AF; background: #FAFAFA; border: 1.5px dashed #D1D5DB; border-radius: 0.75rem;">
      <p style="font-size: 0.9375rem; margin-bottom: 0.25rem; color: #6B7280; font-weight: 500;">No campaigns found</p>
      <p style="font-size: 0.8125rem;">Try adjusting your search or filters.</p>
    </div>

    <div v-else style="display: flex; flex-direction: column; gap: 0.5rem;">
      <div
        v-for="campaign in campaigns"
        :key="campaign.id"
        style="border-radius: 0.625rem; overflow: visible; box-shadow: 0 1px 3px rgba(0,0,0,0.08); border: 1px solid #E5E7EB;"
      >
        <div
          style="display: flex; align-items: center; padding: 0.75rem 1rem; gap: 0.875rem; background: white; border-left: 4px solid #EC4899;"
          @mouseover="$event.currentTarget.style.background='#FAFAFA'"
          @mouseout="$event.currentTarget.style.background='white'"
        >
          <!-- Icon -->
          <div :style="{ flexShrink: 0, width: '2.5rem', height: '2.5rem', borderRadius: '0.625rem', background: campaign.children_node_count > 0 ? 'linear-gradient(135deg, #EC4899, #DB2777)' : '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: campaign.children_node_count > 0 ? '0 2px 6px rgba(236, 72, 153, 0.3)' : 'none', border: campaign.children_node_count > 0 ? 'none' : '1px solid #E5E7EB', overflow: 'hidden' }">
            <img v-if="campaign.thumbnail_logo" :src="campaign.thumbnail_logo" style="width: 100%; height: 100%; object-fit: cover;" />
            <svg v-else-if="campaign.children_node_count > 0" xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: white;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>

          <!-- Info -->
          <div style="flex: 1; min-width: 0;">
            <div style="display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap;">
              <a :href="'/campaigns/' + campaign.id" target="_blank" style="font-size: 0.9375rem; font-weight: 700; color: #111827; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#111827'">
                {{ campaign.name }}
              </a>
              <span style="font-size: 0.625rem; padding: 0.125rem 0.5rem; background: #F3F4F6; color: #6B7280; border-radius: 9999px; font-weight: 500;">
                #{{ campaign.id }}
              </span>
              <span v-if="campaign.children_node_count > 0" @click.prevent="toggleChildren(campaign)" style="font-size: 0.625rem; padding: 0.125rem 0.5rem; background: linear-gradient(135deg, #FDF2F8, #FCE7F3); color: #BE185D; border-radius: 9999px; font-weight: 600; border: 1px solid #FBCFE8; cursor: pointer;" @mouseover="$event.target.style.background='linear-gradient(135deg, #FCE7F3, #FBCFE8)'" @mouseout="$event.target.style.background='linear-gradient(135deg, #FDF2F8, #FCE7F3)'">
                {{ campaign.children_node_count }} sub {{ expandedCampaigns[campaign.id] ? '▾' : '▸' }}
              </span>
            </div>

            <!-- Breadcrumbs -->
            <div v-if="getBreadcrumbsArray(campaign).length > 0" style="display: flex; align-items: center; gap: 0.25rem; margin-top: 0.1875rem; flex-wrap: wrap;">
              <template v-for="(crumb, ci) in getBreadcrumbsArray(campaign)" :key="ci">
                <a :href="'/campaigns/' + crumb.id" target="_blank" style="font-size: 0.6875rem; color: #EC4899; text-decoration: none;" @mouseover="$event.target.style.color='#BE185D'" @mouseout="$event.target.style.color='#EC4899'">{{ truncate(crumb.name, 30) }}</a>
                <span v-if="ci < getBreadcrumbsArray(campaign).length - 1" style="font-size: 0.625rem; color: #D1D5DB;">›</span>
              </template>
            </div>

            <!-- Meta row -->
            <div style="display: flex; align-items: center; gap: 0.75rem; margin-top: 0.25rem; flex-wrap: wrap;">
              <span v-if="campaign.business_name" style="font-size: 0.75rem; color: #6B7280;">
                {{ campaign.business_name }}
              </span>
              <span v-if="campaign.city || campaign.state" style="font-size: 0.75rem; color: #9CA3AF;">
                {{ campaign.city }}{{ campaign.city && campaign.state ? ', ' : '' }}{{ campaign.state }}
              </span>
              <span v-if="campaign.user_name" style="font-size: 0.75rem; color: #9CA3AF; display: flex; align-items: center; gap: 0.25rem;">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.6875rem; height: 0.6875rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                {{ campaign.user_name }}
              </span>
            </div>
          </div>

          <!-- Stats columns -->
          <div style="display: flex; gap: 1.5rem; flex-shrink: 0; align-items: center;">
            <!-- Goal -->
            <div style="text-align: right; display: none;" class="xl:block">
              <div style="font-size: 0.8125rem; font-weight: 600; color: #374151;">${{ formatNum(campaign.goal) }}</div>
              <div style="font-size: 0.5625rem; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em;">Goal</div>
            </div>
            <!-- Raised -->
            <div style="text-align: right;">
              <div style="font-size: 0.9375rem; font-weight: 700; color: #111827;">${{ formatNum(campaign.total_raised_directly_in_this_campaign) }}</div>
              <div style="font-size: 0.5625rem; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em;">Raised</div>
            </div>
            <!-- Event date -->
            <div style="text-align: right; display: none;" class="xl:block">
              <div style="font-size: 0.8125rem; color: #6B7280;">{{ getEventDate(campaign) || '—' }}</div>
              <div style="font-size: 0.5625rem; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em;">Event</div>
            </div>
            <!-- Created -->
            <div style="text-align: right; display: none;" class="xl:block">
              <div style="font-size: 0.8125rem; color: #6B7280;">{{ formatDate(campaign.created_at) }}</div>
              <div style="font-size: 0.5625rem; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.05em;">Created</div>
            </div>
          </div>

          <!-- Actions -->
          <div style="flex-shrink: 0;">
            <campaign-search-crud-button :user="user" :campaign="campaign" :key="campaign.id"></campaign-search-crud-button>
          </div>
        </div>

        <!-- Expand/collapse toggle for campaigns with children -->
        <div
          v-if="campaign.children_node_count > 0"
          style="display: flex; align-items: center; justify-content: center; padding: 0.4375rem; background: #F9FAFB; border-top: 1px solid #E5E7EB; cursor: pointer; user-select: none;"
          @click="toggleChildren(campaign)"
          @mouseover="$event.currentTarget.style.background='#F3F4F6'"
          @mouseout="$event.currentTarget.style.background='#F9FAFB'"
        >
          <svg xmlns="http://www.w3.org/2000/svg" :style="{ width: '0.75rem', height: '0.75rem', color: '#9CA3AF', transition: 'transform 0.2s', transform: expandedCampaigns[campaign.id] ? 'rotate(180deg)' : 'rotate(0)' }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
          <span style="font-size: 0.6875rem; color: #9CA3AF; margin-left: 0.375rem; font-weight: 500;">
            {{ expandedCampaigns[campaign.id] ? 'Hide' : 'Show' }} {{ campaign.children_node_count }} supporting campaign{{ campaign.children_node_count !== 1 ? 's' : '' }}
          </span>
        </div>

        <!-- Loading children indicator -->
        <div v-if="campaign.children_node_count > 0 && expandedCampaigns[campaign.id] && childrenLoading[campaign.id] && !childrenData[campaign.id]" style="background: #F8FAFC; border-top: 1px solid #E5E7EB; padding: 0.625rem 1.125rem 0.625rem 3.75rem; font-size: 0.8125rem; color: #9CA3AF;">
          Loading {{ campaign.children_node_count }} supporting campaign{{ campaign.children_node_count !== 1 ? 's' : '' }}...
        </div>

        <!-- ─── CHILDREN (nested hierarchy, shown on expand) ─── -->
        <div v-if="campaign.children_node_count > 0 && expandedCampaigns[campaign.id] && childrenData[campaign.id] && childrenData[campaign.id].length > 0" style="background: #F8FAFC; border-top: 1px solid #E5E7EB;">
          <!-- Section label -->
          <div style="padding: 0.5rem 1.125rem 0.25rem 3.75rem; font-size: 0.625rem; text-transform: uppercase; letter-spacing: 0.08em; color: #9CA3AF; font-weight: 600; display: flex; justify-content: space-between;">
            <span>Supporting Campaigns</span>
            <span v-if="childrenMeta[campaign.id]" style="text-transform: none; letter-spacing: normal;">{{ childrenData[campaign.id].length }} of {{ childrenMeta[campaign.id].total }}</span>
          </div>

          <!-- Loading -->
          <div v-if="childrenLoading[campaign.id]" style="text-align: center; padding: 1rem; color: #9CA3AF; font-size: 0.8125rem;">Loading...</div>

          <!-- Children list -->
          <template v-else>
            <div v-for="(child, ci) in childrenData[campaign.id]" :key="child.id">
              <!-- Child row -->
              <div
                style="display: flex; align-items: center; padding: 0.5rem 1.125rem 0.5rem 3.75rem; gap: 0.625rem;"
                :style="{ borderBottom: (child.children && child.children.length > 0) ? 'none' : (ci < childrenData[campaign.id].length - 1 ? '1px solid #EFF1F3' : 'none') }"
                @mouseover="$event.currentTarget.style.background='#EFF6FF'"
                @mouseout="$event.currentTarget.style.background='transparent'"
              >
                <!-- Connector -->
                <div style="flex-shrink: 0; width: 1rem; display: flex; align-items: center; justify-content: center; color: #D1D5DB;">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M0 0 L0 6 L12 6" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/></svg>
                </div>
                <!-- Icon -->
                <div :style="{ flexShrink: 0, width: '1.75rem', height: '1.75rem', borderRadius: '0.5rem', background: (child.children && child.children.length > 0) ? 'linear-gradient(135deg, #FDF2F8, #FCE7F3)' : 'white', border: (child.children && child.children.length > 0) ? '1.5px solid #FBCFE8' : '1.5px solid #D1D5DB', display: 'flex', alignItems: 'center', justifyContent: 'center' }">
                  <svg v-if="child.children && child.children.length > 0" xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem; color: #EC4899;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem; color: #9CA3AF;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                </div>
                <!-- Name + info -->
                <div style="flex: 1; min-width: 0;">
                  <div style="display: flex; align-items: center; gap: 0.375rem; flex-wrap: wrap;">
                    <a :href="'/campaigns/' + child.id" target="_blank" style="font-size: 0.875rem; font-weight: 600; color: #374151; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#374151'">{{ truncate(child.name, 45) }}</a>
                    <span v-if="child.children && child.children.length > 0" style="font-size: 0.625rem; padding: 0.125rem 0.375rem; background: linear-gradient(135deg, #FDF2F8, #FCE7F3); color: #BE185D; border-radius: 9999px; font-weight: 600; border: 1px solid #FBCFE8;">{{ child.children.length }} sub</span>
                  </div>
                  <div v-if="child.city || child.state || child.business_name" style="font-size: 0.75rem; color: #9CA3AF; margin-top: 0.0625rem;">{{ child.business_name ? child.business_name + ' · ' : '' }}{{ child.city }}{{ child.city && child.state ? ', ' : '' }}{{ child.state }}</div>
                </div>
                <!-- Amount -->
                <div style="text-align: right; flex-shrink: 0;">
                  <div style="font-size: 0.875rem; font-weight: 600; color: #374151;">${{ formatNum(child.total_raised_with_children_campaigns) }}</div>
                  <div style="height: 0.25rem; width: 50px; background: #F3F4F6; border-radius: 9999px; overflow: hidden; margin-top: 0.25rem; margin-left: auto;">
                    <div :style="{ height: '100%', background: '#EC4899', borderRadius: '9999px', width: Math.min(child.funded || 0, 100) + '%' }"></div>
                  </div>
                </div>
                <!-- Actions -->
                <a :href="'/edit-campaign/' + child.id" style="padding: 0.25rem; color: #C4C9D1; text-decoration: none;" @mouseover="$event.target.closest('a').style.color='#EC4899'" @mouseout="$event.target.closest('a').style.color='#C4C9D1'" title="Edit">
                  <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.8125rem; height: 0.8125rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                </a>
              </div>

              <!-- Grandchildren (third level) -->
              <div v-if="child.children && child.children.length > 0">
                <div v-for="(gc, gi) in child.children" :key="gc.id"
                  style="display: flex; align-items: center; padding: 0.4375rem 1.125rem 0.4375rem 6.5rem; gap: 0.5rem;"
                  :style="{ borderBottom: (gi < child.children.length - 1) ? '1px solid #F3F4F6' : (ci < childrenData[campaign.id].length - 1 ? '1px solid #EFF1F3' : 'none') }"
                  @mouseover="$event.currentTarget.style.background='#F0F9FF'"
                  @mouseout="$event.currentTarget.style.background='transparent'"
                >
                  <div style="flex-shrink: 0; width: 0.75rem; display: flex; align-items: center; justify-content: center; color: #E5E7EB;">
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M0 0 L0 5 L10 5" stroke="currentColor" stroke-width="1.25" fill="none" stroke-linecap="round"/></svg>
                  </div>
                  <div style="flex-shrink: 0; width: 1.25rem; height: 1.25rem; border-radius: 0.25rem; background: white; border: 1px solid #E5E7EB; display: flex; align-items: center; justify-content: center;">
                    <svg xmlns="http://www.w3.org/2000/svg" style="width: 0.625rem; height: 0.625rem; color: #C4C9D1;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                  </div>
                  <div style="flex: 1; min-width: 0;">
                    <a :href="'/campaigns/' + gc.id" target="_blank" style="font-size: 0.75rem; font-weight: 500; color: #6B7280; text-decoration: none;" @mouseover="$event.target.style.color='#EC4899'" @mouseout="$event.target.style.color='#6B7280'">{{ truncate(gc.name, 35) }}</a>
                    <span v-if="gc.city || gc.state" style="font-size: 0.6875rem; color: #C4C9D1; margin-left: 0.25rem;">{{ gc.city }}{{ gc.city && gc.state ? ', ' : '' }}{{ gc.state }}</span>
                  </div>
                  <div style="font-size: 0.75rem; font-weight: 500; color: #9CA3AF; flex-shrink: 0;">${{ formatNum(gc.total_raised_with_children_campaigns) }}</div>
                </div>
              </div>
            </div>
          </template>

          <!-- Load more / View all -->
          <div v-if="childrenMeta[campaign.id] && childrenMeta[campaign.id].total > childrenData[campaign.id].length" style="display: flex; justify-content: center; gap: 0.75rem; padding: 0.625rem; border-top: 1px solid #EFF1F3;">
            <button @click="loadMoreChildren(campaign.id)" :disabled="childrenLoading[campaign.id]" style="padding: 0.375rem 0.75rem; background: white; color: #374151; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.8125rem; cursor: pointer; font-weight: 500;" @mouseover="$event.target.style.borderColor='#EC4899'" @mouseout="$event.target.style.borderColor='#D1D5DB'">Load More</button>
            <a :href="'/sub-campaigns/' + campaign.id" style="padding: 0.375rem 0.75rem; background: #EC4899; color: white; border: none; border-radius: 0.375rem; font-size: 0.8125rem; text-decoration: none; font-weight: 500;" @mouseover="$event.target.style.background='#DB2777'" @mouseout="$event.target.style.background='#EC4899'">View All →</a>
          </div>
        </div>
      </div>
    </div>

    <!-- Load More -->
    <div v-if="next_page_url" style="text-align: center; margin-top: 1.5rem;">
      <button
        @click="loadMore"
        id="loadMore"
        style="padding: 0.625rem 2rem; background: white; color: #374151; border: 1.5px solid #D1D5DB; border-radius: 0.5rem; font-size: 0.875rem; font-weight: 600; cursor: pointer;"
        @mouseover="$event.target.style.borderColor='#EC4899'; $event.target.style.color='#EC4899'"
        @mouseout="$event.target.style.borderColor='#D1D5DB'; $event.target.style.color='#374151'"
      >
        Load More ({{ campaignsData.to }} of {{ campaignsData.total }})
      </button>
    </div>

    <!-- Filter Modal -->
    <transition
      enter-class="transition ease-out duration-100 transform"
      enter-active-class="opacity-0"
      enter-to-class="opacity-100"
      leave-class="transition ease-in duration-200 transform"
      leave-active-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div v-if="openFilterModal" class="fixed z-50 inset-0 overflow-y-auto" id="campaign-search-modal">
        <div class="flex items-start justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div class="fixed inset-0 transition-opacity" aria-hidden="true">
            <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

          <div style="border-radius: 0.75rem; max-width: 28rem;" class="inline-block align-bottom bg-white px-6 pt-6 pb-5 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle w-full" role="dialog" aria-modal="true">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.25rem;">
              <h3 style="font-size: 1.125rem; font-weight: 700; color: #111827;">Filter Results</h3>
              <button @click="openFilterModal = false" style="padding: 0.375rem; color: #9CA3AF; background: none; border: none; cursor: pointer; border-radius: 0.25rem;" @mouseover="$event.target.style.color='#374151'" @mouseout="$event.target.style.color='#9CA3AF'">
                <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div style="display: flex; flex-direction: column; gap: 1rem;">
              <!-- Campaign ID -->
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Campaign ID</label>
                <input v-model="campaignId" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
              <!-- Date Range -->
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Date Range</label>
                <flat-pickr v-model="date" :config="{mode: 'range'}" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;"></flat-pickr>
              </div>
              <!-- Campaign Type -->
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Campaign Type</label>
                <select v-model="campaignType" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; background: white;">
                  <option value="">All Types</option>
                  <option value="1">Dig Pink</option>
                  <option value="2">Pledge Drive</option>
                  <option value="3">Standard</option>
                </select>
              </div>
              <!-- State -->
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">State</label>
                <select v-model="state" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; background: white;">
                  <option value="">All States</option>
                  <option value="AL">Alabama</option><option value="AK">Alaska</option><option value="AZ">Arizona</option><option value="AR">Arkansas</option><option value="CA">California</option><option value="CO">Colorado</option><option value="CT">Connecticut</option><option value="DE">Delaware</option><option value="DC">District of Columbia</option><option value="FL">Florida</option><option value="GA">Georgia</option><option value="HI">Hawaii</option><option value="ID">Idaho</option><option value="IL">Illinois</option><option value="IN">Indiana</option><option value="IA">Iowa</option><option value="KS">Kansas</option><option value="KY">Kentucky</option><option value="LA">Louisiana</option><option value="ME">Maine</option><option value="MD">Maryland</option><option value="MA">Massachusetts</option><option value="MI">Michigan</option><option value="MN">Minnesota</option><option value="MS">Mississippi</option><option value="MO">Missouri</option><option value="MT">Montana</option><option value="NE">Nebraska</option><option value="NV">Nevada</option><option value="NH">New Hampshire</option><option value="NJ">New Jersey</option><option value="NM">New Mexico</option><option value="NY">New York</option><option value="NC">North Carolina</option><option value="ND">North Dakota</option><option value="OH">Ohio</option><option value="OK">Oklahoma</option><option value="OR">Oregon</option><option value="PA">Pennsylvania</option><option value="RI">Rhode Island</option><option value="SC">South Carolina</option><option value="SD">South Dakota</option><option value="TN">Tennessee</option><option value="TX">Texas</option><option value="UT">Utah</option><option value="VT">Vermont</option><option value="VA">Virginia</option><option value="WA">Washington</option><option value="WV">West Virginia</option><option value="WI">Wisconsin</option><option value="WY">Wyoming</option>
                </select>
              </div>
              <!-- Total Raised -->
              <div>
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151; display: block; margin-bottom: 0.25rem;">Total Raised ($)</label>
                <input v-model="totalRaised" type="text" style="width: 100%; padding: 0.5rem 0.75rem; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem;" />
              </div>
              <!-- Top-level only -->
              <div style="display: flex; align-items: center; gap: 0.5rem;">
                <input v-model="doNotShowSubCampaign" type="checkbox" style="width: 1rem; height: 1rem; accent-color: #EC4899;" />
                <label style="font-size: 0.8125rem; font-weight: 500; color: #374151;">Only show top-level campaigns</label>
              </div>
            </div>

            <div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.25rem;">
              <button @click="resetFilters" style="padding: 0.5rem 1rem; background: white; color: #6B7280; border: 1.5px solid #D1D5DB; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 500; cursor: pointer;">Reset</button>
              <button @click="search" style="padding: 0.5rem 1rem; background: #EC4899; color: white; border: none; border-radius: 0.375rem; font-size: 0.875rem; font-weight: 600; cursor: pointer;" @mouseover="$event.target.style.background='#DB2777'" @mouseout="$event.target.style.background='#EC4899'">Apply Filters</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Export Modal -->
    <div v-if="isExporting || showExportModal" class="fixed inset-0 z-50" style="display: flex; align-items: center; justify-content: center;">
      <div style="position: absolute; inset: 0; background: rgba(0,0,0,0.4);"></div>
      <div style="position: relative; background: white; padding: 1.5rem; border-radius: 0.75rem; box-shadow: 0 20px 25px rgba(0,0,0,0.15); width: 24rem; max-width: 90vw;">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;">
          <h3 style="font-size: 1rem; font-weight: 600; color: #111827;">{{ exportStatus }}</h3>
          <button @click="hideExportModal" style="padding: 0.25rem; color: #9CA3AF; background: none; border: none; cursor: pointer;">
            <svg xmlns="http://www.w3.org/2000/svg" style="width: 1.25rem; height: 1.25rem;" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div style="width: 100%; height: 0.5rem; background: #F3F4F6; border-radius: 9999px; overflow: hidden; margin-bottom: 0.75rem;">
          <div :style="{ width: exportProgress + '%', height: '100%', background: 'linear-gradient(90deg, #EC4899, #DB2777)', borderRadius: '9999px', transition: 'width 0.3s ease' }"></div>
        </div>
        <p style="font-size: 0.8125rem; color: #6B7280;">{{ exportMessage }}</p>
      </div>
    </div>

  </div>
</template>

<script>
import moment from "moment";
import flatPickr from 'vue-flatpickr-component';
import VueScrollTo from 'vue-scrollto';

export default {
  props: {
    allCampaigns: {},
    user: {},
  },
  components: {
    flatPickr
  },
  data() {
    return {
      campaignsData: this.allCampaigns,
      campaigns: this.allCampaigns?.data,
      isOpen: false,
      openFilterModal: false,
      next_page_url: this.allCampaigns?.next_page_url ? '/campaign-search-with-term?page=2' : '',
      searchTerm: '',
      doNotShowSubCampaign: 0,
      totalRaised: '',
      state: '',
      campaignType: '',
      campaignId: '',
      date: '',
      filterCount: 0,
      expandedCampaigns: {},
      childrenData: {},
      childrenMeta: {},
      childrenLoading: {},
      childrenPages: {},
      isExporting: false,
      showExportModal: false,
      exportStatus: '',
      exportProgress: 0,
      exportMessage: '',
      exportCheckInterval: null,
    }
  },
  methods: {
    toggleChildren(campaign) {
      if (this.expandedCampaigns[campaign.id]) {
        this.expandedCampaigns[campaign.id] = false;
      } else {
        this.expandedCampaigns[campaign.id] = true;
        // Fetch children if not already loaded
        if (!this.childrenData[campaign.id]) {
          this.childrenPages[campaign.id] = 1;
          this.fetchChildren(campaign.id, false);
        }
      }
    },
    fetchChildren(campaignId, append) {
      this.childrenLoading[campaignId] = true;
      const page = this.childrenPages[campaignId] || 1;
      axios.get('/get-foundation-children/' + campaignId + '?page=' + page).then(response => {
        const data = response.data;
        if (append && this.childrenData[campaignId]) {
          this.childrenData[campaignId] = this.childrenData[campaignId].concat(data.data);
        } else {
          this.childrenData[campaignId] = data.data;
        }
        this.childrenMeta[campaignId] = { total: data.total, lastPage: data.last_page };
        this.childrenLoading[campaignId] = false;
      }).catch(() => {
        this.childrenLoading[campaignId] = false;
      });
    },
    loadMoreChildren(campaignId) {
      const currentPage = this.childrenPages[campaignId] || 1;
      this.childrenPages[campaignId] = currentPage + 1;
      this.fetchChildren(campaignId, true);
    },
    getBreadcrumbsArray(campaign) {
      if (!campaign.breadcrumbs) return [];
      try {
        let breadcrumbs = JSON.parse(campaign.breadcrumbs);
        if (!breadcrumbs) return [];
        let arr = Array.isArray(breadcrumbs) ? breadcrumbs : Object.values(breadcrumbs);
        return arr.filter(b => b && b.id != campaign.id);
      } catch (e) {
        return [];
      }
    },
    truncate(str, len) {
      if (!str) return '';
      return str.length > len ? str.substring(0, len) + '…' : str;
    },
    formatDate(date) {
      return moment(date).format('MM-DD-YYYY');
    },
    formatNum(n) {
      if (!n && n !== 0) return '0';
      return Number(n).toLocaleString();
    },
    getFilterRequestQuery() {
      this.filterCount = 0;
      let query = '';
      if (this.doNotShowSubCampaign == 1) { query += '&doNotShowSubCampaign=1'; this.filterCount++; }
      if (this.totalRaised) { query += '&totalRaised=' + this.totalRaised; this.filterCount++; }
      if (this.state) { query += '&state=' + this.state; this.filterCount++; }
      if (this.campaignType) { query += '&campaignType=' + this.campaignType; this.filterCount++; }
      if (this.campaignId) { query += '&campaignId=' + this.campaignId; this.filterCount++; }
      if (this.date) { query += '&date=' + this.date; this.filterCount++; }
      return query;
    },
    resetFilters() {
      this.doNotShowSubCampaign = '';
      this.totalRaised = '';
      this.state = '';
      this.campaignType = '';
      this.campaignId = '';
      this.date = null;
    },
    search() {
      // Clear old children data on new search
      this.childrenData = {};
      this.childrenMeta = {};
      this.childrenLoading = {};
      this.childrenPages = {};
      this.expandedCampaigns = {};
      axios.get('/campaign-search-with-term?search_term=' + this.searchTerm + this.getFilterRequestQuery())
        .then(response => {
          this.campaigns = response.data.data;
          this.campaignsData = response.data;
          this.next_page_url = response.data.next_page_url;
          this.openFilterModal = false;
        });
    },
    loadMore() {
      if (this.next_page_url) {
        axios.get(this.next_page_url + '&search_term=' + this.searchTerm)
          .then(response => {
            this.campaignsData = response.data;
            const newCampaigns = response.data.data;
            newCampaigns.forEach(v => this.campaigns.push(v));
            this.next_page_url = response.data.next_page_url;
            if (this.next_page_url) {
              VueScrollTo.scrollTo('#loadMore', { duration: 1500, offset: -100 });
            }
          });
      }
    },
    getEventDate(campaign) {
      if (campaign.meta_data) {
        try {
          let data = JSON.parse(campaign.meta_data);
          if (data.event_date) return this.formatDate(data.event_date);
        } catch (e) {}
      }
      if (campaign.event_date) return this.formatDate(campaign.event_date);
      return null;
    },
    download() {
      this.isExporting = true;
      this.showExportModal = true;
      this.exportStatus = 'Starting export...';
      this.exportProgress = 0;
      axios.post('/campaign-search-export?search_term=' + this.searchTerm + this.getFilterRequestQuery(), {
        file_name: 'campaigns-export.xlsx'
      }).then(() => {
        this.checkExportStatus();
      }).catch(() => {
        this.isExporting = false;
        this.exportStatus = 'Export failed. Please try again.';
      });
    },
    hideExportModal() {
      this.showExportModal = false;
      this.exportProgress = 0;
      this.exportMessage = '';
      this.exportStatus = '';
      this.isExporting = false;
      if (this.exportCheckInterval) {
        clearInterval(this.exportCheckInterval);
        this.exportCheckInterval = null;
      }
    },
    checkExportStatus() {
      if (!this.exportCheckInterval) {
        this.exportCheckInterval = setInterval(() => {
          axios.get('/campaign-export-status').then(response => {
            if (response.data.status === 'completed') {
              this.isExporting = false;
              this.exportStatus = 'Export completed!';
              this.exportProgress = 100;
              window.location.href = response.data.download_url;
              setTimeout(() => this.hideExportModal(), 2000);
            } else if (response.data.status === 'pending' || response.data.status === 'queued') {
              this.exportStatus = response.data.message || 'Processing...';
              this.exportProgress = response.data.progress || 0;
            } else {
              clearInterval(this.exportCheckInterval);
              this.exportCheckInterval = null;
              this.isExporting = false;
              this.exportStatus = 'Export failed';
            }
          }).catch(() => {
            clearInterval(this.exportCheckInterval);
            this.exportCheckInterval = null;
            this.isExporting = false;
            this.exportStatus = 'Export failed';
          });
        }, 2000);
      }
    },
  },
}
</script>
